import type { TruckClassInput } from '@chukua/common';
import { Button, Table } from 'antd';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { TableSkeleton } from '../../components/TableSkeleton';
import { buildTruckClassColumns } from './build-truck-class-columns';
import type { TruckClass } from './reference-data.api';
import { TruckClassFormDrawer } from './TruckClassFormDrawer';

interface TruckClassesPanelProps {
  classes: TruckClass[];
  loading: boolean;
  saveError: string | null;
  saving: boolean;
  onCreate: (input: TruckClassInput) => Promise<boolean>;
  onUpdate: (id: string, input: TruckClassInput) => Promise<boolean>;
  onDeactivate: (row: TruckClass) => Promise<void>;
  onReactivate: (row: TruckClass) => Promise<void>;
  onResetSaveError: () => void;
}

/** Client-side paging (Ant's default) — the small-fixed-catalogue exception to server-side lists (CLAUDE.md → Lists and tables). */
export function TruckClassesPanel({
  classes,
  loading,
  saveError,
  saving,
  onCreate,
  onUpdate,
  onDeactivate,
  onReactivate,
  onResetSaveError,
}: TruckClassesPanelProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<TruckClass | undefined>(undefined);

  function openCreate() {
    onResetSaveError();
    setEditing(undefined);
    setDrawerOpen(true);
  }

  function openEdit(row: TruckClass) {
    onResetSaveError();
    setEditing(row);
    setDrawerOpen(true);
  }

  const columns = buildTruckClassColumns({ onEdit: openEdit, onDeactivate, onReactivate });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <Button type="primary" icon={<Plus className="size-4" />} onClick={openCreate}>
          New truck class
        </Button>
      </div>
      {loading ? (
        <TableSkeleton label="Loading truck classes" />
      ) : (
        <Table rowKey="id" dataSource={classes} columns={columns} pagination={{ pageSize: 25 }} scroll={{ x: 'max-content' }} />
      )}
      <TruckClassFormDrawer
        open={drawerOpen}
        editing={editing}
        error={saveError}
        submitting={saving}
        onSubmit={(input) => (editing ? onUpdate(editing.id, input) : onCreate(input))}
        onClose={() => setDrawerOpen(false)}
      />
    </div>
  );
}
